import axios, { AxiosError } from 'axios'
import * as Sentry from '@sentry/react'
import { getStore } from '../../../../state/configure_store'
import { maintenanceStarted } from '../../../../state/slices/maintenance_slice'
import { setNetworkFailures } from '../../../../state/slices/temporary_data_slice'

type IError = {
  message?: string
  title?: string
  error?: string
  description?: string
}

type IMaintenanceError = {
  response?: {
    data?: {
      data?: {
        description?: string
        title?: string
      }
    }
  }
}

const SENTRY_BLACK_LIST = [
  'De link om het wachtwoord in te stellen is niet meer geldig. Probeer opnieuw je wachtwoord te resetten.',
  'Er bestaat al een gebruiker voor het opgegeven e-mailadres.',
  'De door u ingevulde gegevens is/zijn onjuist en/of niet volledig ingevuld',
  'Emailadres en/of wachtwoord combinatie komt niet overeen.',
  'Uw sessie is verlopen.'
]

const STATUS_CODE_BLACK_LIST = [503, 401, 429]

const parseErrorMessage = (error: AxiosError<IError>): string => {
  if (error.response?.data) {
    if (typeof error.response.data === 'string') {
      return error.response.data // If API returns a plain string
    } else if (error.response.data.message) {
      return error.response.data.message // If API returns { message: "Error details" }
    } else if (error.response.data.error) {
      return error.response.data.error // If API returns { error: "Some error" }
    }
  }

  return error.message
}

const parseParams = (data: any): string => {
  try {
    return typeof data === 'string' ? data : JSON.stringify(data)
  } catch {
    return 'Unserializable params'
  }
}

export const responseErrorInterceptor = (error: AxiosError<IError>) => {
  const isCancelled =
    error.code === 'ERR_CANCELED' ||
    axios.isCancel(error)

  if (isCancelled) {
    return Promise.reject(error)
  }

  const statusCode = error.response?.status || 0
  const config = error.config
  const method = config?.method || 'UNKNOWN_METHOD'
  const apiUrl = config?.url || 'UNKNOWN_URL'
  const apiMethod = apiUrl.split('/').pop()
  const params = parseParams(config?.data)
  let customMessage = parseErrorMessage(error)

  if (statusCode === 503) {
    const store = getStore()

    const maintenanceError = error as IMaintenanceError
    const responseData = maintenanceError.response?.data?.data
    const responseMessage = responseData?.description || 'Probeer het later opnieuw.'
    const responseTitle = responseData?.title || 'Onderhoudswerkzaamheden'

    store.dispatch(maintenanceStarted({
      active: true,
      message: responseMessage,
      title: responseTitle
    }))

    customMessage = `<strong>${responseTitle}</strong></br>${responseMessage}`
  } else if (!SENTRY_BLACK_LIST.includes(customMessage) && !STATUS_CODE_BLACK_LIST.includes(statusCode)) {
    Sentry.captureException(new Error(customMessage), {
      extra: {
        statusCode,
        method,
        url: apiUrl,
        params,
        message: customMessage,
        apiMethod,
        error: error.toJSON()
      }
    })
  }

  if (error.code === 'ERR_NETWORK') {
    const store = getStore()

    store.dispatch(
      setNetworkFailures(
        store.getState().temporaryData.networkFailures + 1
      )
    )
  }

  error.message = customMessage

  return Promise.reject(error)
}
